import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Backspace, Check } from '@phosphor-icons/react';

/**
 * On-screen numeric keypad optimised for logging a weight, rep count, or duration
 * mid-session. Includes optional RPE 5-10 quick-select above the display and
 * quick common-value chips on the right.
 *
 * Props:
 *  - title           string   e.g. "Weight" | "Reps" | "Distance"
 *  - unit            string   'kg' | 'reps' | 'm' | 's' | ''
 *  - initial         number
 *  - allowDecimal    bool
 *  - quickValues     number[]  common reps [5,6,8,10,12] or weights [20,40,60,80,100]
 *  - showRpe         bool     append RPE 5-10 chip row and return { value, rpe }
 *  - initialRpe      number|null
 *  - onSubmit        (number|{value,rpe}) => void
 *  - onClose         () => void
 */
export default function NumericKeypad({
  title,
  unit = '',
  initial = 0,
  allowDecimal = false,
  quickValues = [],
  showRpe = false,
  initialRpe = null,
  onSubmit,
  onClose,
  testIdPrefix = 'keypad',
}) {
  const [text, setText] = useState(String(initial ?? ''));
  const [rpe, setRpe] = useState(initialRpe);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setText(String(initial ?? ''));
  }, [initial]);

  const press = (ch) => {
    setTouched(true);
    setText((prev) => {
      const startVal = touched ? prev : '';
      if (ch === '.' && startVal.includes('.')) return startVal;
      if (ch === '.' && startVal === '') return '0.';
      const next = startVal + ch;
      // guard: max 6 chars keeps display sane
      if (next.length > 6) return startVal;
      return next;
    });
  };

  const backspace = () => {
    setTouched(true);
    setText((prev) => prev.slice(0, -1));
  };

  const clear = () => { setTouched(true); setText(''); };

  const submit = () => {
    const val = parseFloat(text || '0');
    if (isNaN(val)) return;
    if (showRpe) onSubmit({ value: val, rpe });
    else onSubmit(val);
  };

  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [allowDecimal ? '.' : '', '0', 'back'],
  ];

  return createPortal(
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end justify-center px-3 pb-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }} data-testid={`${testIdPrefix}-modal`}>
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 pt-4 pb-3 border-b border-hairline flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">Enter</p>
            <h2 className="text-2xl font-black font-heading uppercase text-graphite leading-none mt-0.5">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
            data-testid={`${testIdPrefix}-close`}
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* RPE chips above the display (as requested) */}
        {showRpe && (
          <div className="px-4 pt-3 pb-1">
            <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">RPE — how hard was it?</p>
            <div className="grid grid-cols-6 gap-1.5">
              {[5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  onClick={() => setRpe(rpe === n ? null : n)}
                  className={`h-10 rounded-ios-pill text-sm font-black tabular-nums font-heading transition-all ${
                    rpe === n ? 'bg-userRed text-bone shadow-ios' : 'bg-graphite/5 text-graphite/60 hover:bg-graphite/10'
                  }`}
                  data-testid={`${testIdPrefix}-rpe-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Value display */}
        <div className="px-5 pt-3 pb-2 flex items-baseline justify-center gap-2" onClick={clear}>
          <span
            className={`text-6xl font-black font-heading tabular-nums text-graphite leading-none transition-all ${
              !touched ? 'keypad-selected' : ''
            }`}
            data-testid={`${testIdPrefix}-display`}
            title={!touched ? 'Type to replace' : ''}
          >
            {text || '0'}
          </span>
          {unit && (
            <span className="text-2xl font-normal text-graphite/50">{unit}</span>
          )}
        </div>
        {!touched && (
          <p className="text-center -mt-1 mb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-userRed/80">
            Type to replace
          </p>
        )}

        {/* Quick-values row */}
        {quickValues.length > 0 && (
          <div className="px-4 pb-3 flex gap-1.5 flex-wrap justify-center">
            {quickValues.map((qv) => (
              <button
                key={qv}
                onClick={() => { setTouched(true); setText(String(qv)); }}
                className="h-8 px-3 rounded-ios-pill text-[11px] uppercase tracking-[0.15em] font-bold bg-graphite/5 hover:bg-graphite text-graphite/60 hover:text-bone tabular-nums"
                data-testid={`${testIdPrefix}-quick-${qv}`}
              >
                {qv}
              </button>
            ))}
          </div>
        )}

        {/* Keypad */}
        <div className="px-4 pb-4 grid grid-cols-3 gap-2">
          {keys.flat().map((k, i) => {
            if (k === '') return <div key={i} />;
            if (k === 'back') {
              return (
                <button
                  key={i}
                  onClick={backspace}
                  className="h-14 rounded-ios-lg bg-graphite/8 hover:bg-graphite/12 flex items-center justify-center text-graphite"
                  data-testid={`${testIdPrefix}-back`}
                >
                  <Backspace size={20} weight="bold" />
                </button>
              );
            }
            return (
              <button
                key={i}
                onClick={() => press(k)}
                className="h-14 rounded-ios-lg bg-graphite/5 hover:bg-graphite/12 text-2xl font-black font-heading tabular-nums text-graphite active:scale-95 transition-transform"
                data-testid={`${testIdPrefix}-key-${k}`}
              >
                {k}
              </button>
            );
          })}
        </div>

        <button
          onClick={submit}
          className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-2"
          data-testid={`${testIdPrefix}-submit`}
        >
          <Check size={16} weight="bold" />
          Done
        </button>
      </div>
    </div>,
    document.body
  );
}
